import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { SideNav } from "@/components/SideNav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pin, PinOff, Pencil, Trash2, MessageCircle } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function AdminClientNotes() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [text, setText] = useState("");
  const [pinned, setPinned] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/client-notes");
      setNotes(data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setText(""); setPinned(false); setOpenForm(true); };
  const openEdit = (n) => { setEditing(n); setText(n.text); setPinned(!!n.pinned); setOpenForm(true); };

  const save = async () => {
    const t = text.trim();
    if (!t) return toast.error("Note text required");
    try {
      if (editing) {
        await api.patch(`/client-notes/${editing.id}`, { text: t, pinned });
        toast.success("Note updated");
      } else {
        await api.post("/client-notes", { text: t, pinned });
        toast.success("Note posted");
      }
      setOpenForm(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const togglePin = async (n) => {
    try {
      await api.patch(`/client-notes/${n.id}`, { pinned: !n.pinned });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/client-notes/${confirmDel.id}`);
      toast.success("Note removed");
      setConfirmDel(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AppHeader title="Notes to Client" subtitle="What CCI sees on their dashboard" />
      <div className="flex-1 flex">
        <SideNav />
        <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 py-6 space-y-4">
          <Card className="p-5 rounded-xl border-gray-200 shadow-sm bg-white flex items-center justify-between">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Client-visible updates</div>
              <div className="font-display text-xl font-bold text-gray-900 mt-0.5">{notes.length} note{notes.length === 1 ? "" : "s"}</div>
              <div className="text-xs text-gray-500 mt-1">Text/status only — no cost or margin figures leaked to Client.</div>
            </div>
            <Button data-testid="new-client-note-btn" onClick={openNew} className="bg-brand-red hover:bg-[#B31721] text-white">
              <Plus className="h-4 w-4 mr-1.5" /> New note
            </Button>
          </Card>

          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading…</div>
          ) : notes.length === 0 ? (
            <Card className="p-10 rounded-xl border-dashed border-gray-300 text-center bg-white">
              <MessageCircle className="h-8 w-8 mx-auto text-gray-400" />
              <div className="mt-2 text-sm text-gray-500">No notes yet. Post the first update for CCI.</div>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="client-notes-list">
              {notes.map((n) => (
                <Card key={n.id} className="p-4 rounded-xl border-gray-200 bg-white" data-testid={`client-note-${n.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {n.pinned && <Badge className="bg-red-50 text-brand-red border border-red-200"><Pin className="h-3 w-3 mr-1" />Pinned</Badge>}
                        <span className="text-[11px] text-gray-500">{fmtDate(n.created_at)} · by {n.created_by_name}</span>
                      </div>
                      <div className="text-sm text-gray-900 whitespace-pre-wrap break-words">{n.text}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => togglePin(n)} title={n.pinned ? "Unpin" : "Pin"}
                        className="h-8 w-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600"
                        data-testid={`toggle-pin-${n.id}`}>
                        {n.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                      </button>
                      <button onClick={() => openEdit(n)} title="Edit"
                        className="h-8 w-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600"
                        data-testid={`edit-note-${n.id}`}>
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button onClick={() => setConfirmDel(n)} title="Delete"
                        className="h-8 w-8 rounded-lg hover:bg-red-50 flex items-center justify-center text-red-600"
                        data-testid={`del-note-${n.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Note form dialog */}
      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent data-testid="client-note-dialog" className="rounded-2xl">
          <DialogHeader><DialogTitle>{editing ? "Edit note" : "New note to Client"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Note text</label>
              <Textarea data-testid="client-note-text" rows={5}
                placeholder="e.g. Store 12 machine repaired today. New store go-live on 5th."
                value={text} onChange={(e) => setText(e.target.value)} />
              <div className="text-[10px] text-gray-400 mt-1">Visible to Client (CCI) role. Do not include costs, margins, or expenses.</div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" data-testid="client-note-pin" checked={pinned} onChange={(e) => setPinned(e.target.checked)} className="accent-brand-red" />
              Pin at the top of Client dashboard
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenForm(false)}>Cancel</Button>
            <Button data-testid="save-client-note-btn" onClick={save} className="bg-brand-red hover:bg-[#B31721] text-white">
              {editing ? "Save" : "Post note"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent data-testid="client-note-del-dialog" className="rounded-2xl">
          <DialogHeader><DialogTitle>Delete note?</DialogTitle></DialogHeader>
          <div className="text-sm text-gray-700">
            This will remove the note from the Client dashboard immediately. This action is logged in the audit trail.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button data-testid="confirm-del-client-note" onClick={del} className="bg-red-600 hover:bg-red-700 text-white">
              <Trash2 className="h-4 w-4 mr-1.5" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
